import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatherExternalLink } from "@subframe/core";
import * as SubframeUtils from "../../../../ui/utils";
import {
  REPORTS_NAV_SECTIONS,
  ReportsNavItem,
  isReportsNavItemActive,
} from "../config/reportsNavigation";

interface NavLinkProps {
  item: ReportsNavItem;
  active: boolean;
  onSelect: (to: string) => void;
}

const NavLink: React.FC<NavLinkProps> = ({ item, active, onSelect }) => {
  const available = item.to !== undefined;

  return (
    <button
      type="button"
      disabled={!available}
      aria-current={active ? "page" : undefined}
      aria-disabled={!available}
      title={available ? undefined : "Coming soon"}
      onClick={() => item.to && onSelect(item.to)}
      className={SubframeUtils.twClassNames(
        "flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-body font-body transition-colors",
        {
          "bg-neutral-100 text-default-font": active,
          "text-subtext-color hover:bg-neutral-50 hover:text-default-font": available && !active,
          // Not-yet-built reports stay visible so the shape of the area is
          // legible, but must not look clickable.
          "cursor-not-allowed text-neutral-400 hover:bg-transparent": !available,
        }
      )}
    >
      <span className="truncate">{item.label}</span>
      {item.external && <FeatherExternalLink className="shrink-0 text-caption font-caption text-neutral-400" />}
    </button>
  );
};

/** The list of reports, grouped the way Zoho's Reports Center groups them. */
export const ReportsNav: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav className="flex w-full flex-col gap-6">
      {REPORTS_NAV_SECTIONS.map((section) => (
        <div key={section.id} className="flex flex-col gap-1">
          {section.label && (
            <span className="px-3 pb-1 text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color">
              {section.label}
            </span>
          )}
          {section.items.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              active={isReportsNavItemActive(item, pathname)}
              onSelect={navigate}
            />
          ))}
        </div>
      ))}
    </nav>
  );
};
